import React, { useState } from 'react';
import AuthProvider from '../common/services/auth/AuthProvider/Index';
import './ProviderDashboard.css';
import CustomerListModal from '../common/components/CustomerListModal';
import EditCustomerModal from '../common/components/EditCustomerModal';
import { rsaorderKit, updateRsaKitCustomer } from '../api/provider';
import notifications from '../common/services/notifications';
import { PencilIcon, EyeIcon, CheckCircleIcon } from '@heroicons/react/24/outline';
import { usePaginatedKits } from './hooks/usePaginatedKits';
import { capitalizeFirst, formatBirthdate } from './utils/dashboardHelpers';
import { useProviderBootstrap } from '../common/hooks/useProviderBootstrap';
import { Pagination, FilterSidebar } from './components/DashboardCommon';
import KitsTable from './components/KitsTable';
import TableSection from './components/TableSection';
import DashboardLayout from './components/DashboardLayout';
import { createKitFetcher } from './utils/kitApiFetcher';
import { EmptyTableMessage, LoadingSpinner, createSearchKeyHandler } from './utils/dashboardConstants';
import { createPatientNameColumn, createStatusColumn, createResultsColumn, createResultsDateColumn, createOrderDateColumn, createShipDateColumn, createTrackingNumberColumn, type TableColumn, type BaseKit } from './utils/tableColumns';

// Define types for our data
interface Kit extends BaseKit {
  id?: string;
  kitType?: string;
  rsaorder_timestamp: number | null;
  notes: string | null;
  customer_id: string | null;
  customer_phone: string | null;
  customer_address?: string | null;
  customer_address_line2?: string | null;
  customer_city?: string | null;
  customer_state?: string | null;
  customer_zip?: string | null;
  order_date?: number | null;
  ship_date?: number | null;
  tracking_number?: string | null;
}

// Local status mapping for RSA kits
const RSA_STATUS_MAPPING = {
  CLAIM: "claimed",
  SHIPPING_TO_LAB_COMPLETED: "shippingToLabCompleted",
  SUCCESSFULLY_PROCESSED_BY_LAB: "successfullyProcessedByLab",
  COMPLETED: "completed",
};

// Custom status display mappings for RSA dashboard
// Handle both API format (camelCase) and normalized format (uppercase)
const RSA_STATUS_DISPLAY_MAPPINGS: Record<string, { text: string; color: string }> = {
  'claimed': { text: 'Ordered', color: 'status-default' },
  'CLAIMED': { text: 'Ordered', color: 'status-default' },
  'shippingToLabCompleted': { text: 'Shipped', color: 'status-processing' },
  'SHIPPING_TO_LAB_COMPLETED': { text: 'Shipped', color: 'status-processing' },
  'SHIPPINGTOLABCOMPLETED': { text: 'Shipped', color: 'status-processing' },
};

const FILTER_OPTIONS = [
  { label: "All Results", value: null },
  { label: "Ordered", value: RSA_STATUS_MAPPING.CLAIM },
  { label: "Shipped", value: RSA_STATUS_MAPPING.SHIPPING_TO_LAB_COMPLETED },
  { label: "Results Ready", value: RSA_STATUS_MAPPING.SUCCESSFULLY_PROCESSED_BY_LAB },
  { label: "Completed", value: RSA_STATUS_MAPPING.COMPLETED }
];


const ProviderDashboard: React.FC = () => {
  const {
    kits,
    totalResults,
    loading,
    error,
    setError,
    currentPage,
    totalPages,
    selectedFilter,
    searchQuery,
    setSearchQuery,
    handleSearch,
    handlePageChange,
    handleFilterChange,
    handleSort,
    sortBy,
    sortOrder,
    fetchPage,
  } = usePaginatedKits<Kit>(createKitFetcher('rsa-kits'));
  
  const { clinic, provider } = useProviderBootstrap({
    onReady: async () => {
      await fetchPage(1);
    },
    setError,
  });
  const [rsaOrderModalOpen, setRSAOrderModalOpen] = useState(false);
  const [viewResultsModalOpen, setViewResultsModalOpen] = useState(false);
  const [selectedKitForViewResults, setSelectedKitForViewResults] = useState<Kit | null>(null);
  const [pdfUrl, setPdfUrl] = useState<string | null>(null);
  const [isLoadingPdf, setIsLoadingPdf] = useState(false);
  const [pdfFileName, setPdfFileName] = useState<string | null>(null);
  const [isPdfLoading, setIsPdfLoading] = useState(false);
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const [selectedKitForCompletion, setSelectedKitForCompletion] = useState<Kit | null>(null);
  const [isCompletingKit, setIsCompletingKit] = useState(false);
  const [editCustomerModalOpen, setEditCustomerModalOpen] = useState(false);
  const [selectedKitForEdit, setSelectedKitForEdit] = useState<Kit | null>(null);

  const handleKeyDown = createSearchKeyHandler(handleSearch);

  const tableColumns: TableColumn<Kit>[] = [
    createPatientNameColumn<Kit>(),
    {
      header: "Patient Phone",
      className: "kit-id",
      render: (kit: Kit) => (
        <span className="email">{kit.customer_phone ? kit.customer_phone : 'N/A'}</span>
      ),
    },
    createStatusColumn<Kit>(RSA_STATUS_DISPLAY_MAPPINGS),
    createOrderDateColumn<Kit>(true),
    createShipDateColumn<Kit>(true),
    createTrackingNumberColumn<Kit>(),
    createResultsColumn<Kit>(),
    createResultsDateColumn<Kit>(true),
      {
      header: "Actions",
      className: "action-cell sticky-column",
      render: (kit: Kit) => {
        const isReady = kit.has_results && kit.status === RSA_STATUS_MAPPING.SUCCESSFULLY_PROCESSED_BY_LAB;
        const isCompleted = kit.status === RSA_STATUS_MAPPING.COMPLETED;
        return (
          <div className="action-buttons-container">
            <button
              className="edit-customer-button"
              onClick={() => handleEditCustomerClick(kit)}
              title="Edit Patient Details"
            >
              <PencilIcon className="button-icon" />
              Edit
            </button>
            {(isReady || isCompleted) && (
              <button
                className="view-results-button"
                onClick={() => handleViewResultsClick(kit)}
              >
                <EyeIcon className="button-icon" />
                View Results
              </button>
            )}
            {isReady && (
              <button
                className="mark-completed-button"
                onClick={() => handleMarkAsCompletedClick(kit)}
              >
                <CheckCircleIcon className="button-icon" />
                Mark as Completed
              </button>
            )}
          </div>
        );
      },
    },
  ];



  const handleRsaorderKit = async (customerData: {
    firstName: string;
    lastName: string;
    phoneNumber: string;
    dateOfBirth: string;
    ssnLast4: string;
    address: string;
    addressLine2: string;
    city: string;
    state: string;
    zip: string;
  }) => {
    try {
      if (!provider?.sub) {
        throw new Error('Provider information not available');
      }

      if (!clinic?.clinic_id) {
        throw new Error('Clinic information not available');
      }
      
      await rsaorderKit(customerData, provider?.sub, clinic?.clinic_id, clinic?.name);
      // Refresh the kits list to show updated status
      fetchPage(currentPage, searchQuery, selectedFilter);
      setError(null);
      notifications.success({
        message: `RSA Order has been successfully created for customer ${customerData.phoneNumber}.`
      });
      // Close the modal after successful rsaorder
      setRSAOrderModalOpen(false);
    } catch (error: any) {
      console.error('Error creating RSA order:', error);
      
      // Handle specific error cases
      let errorMessage = 'Failed to create RSA order. Please try again.';
      
      if (error?.status === 409) {
        // Customer already exists with this email
        errorMessage = `A customer with phone number ${customerData.phoneNumber} already exists. Please use a different phone number or contact support if you believe this is an error.`;
      } else if (error?.status === 422) {
        // Validation error
        errorMessage = 'Please check your input data and try again.';
      } else if (error?.status === 400) {
        // Bad request
        errorMessage = 'Invalid request. Please check your input data.';
      } else if (error?.message) {
        // Use the specific error message from the API
        errorMessage = error.message;
      }
      
      setError(errorMessage);
      notifications.error({
        message: errorMessage
      });
    }
  };

  const handleViewResultsClick = async (kit: Kit) => {
    setSelectedKitForViewResults(kit);
    setViewResultsModalOpen(true);
    setIsLoadingPdf(true);
    setPdfUrl(null);
    setPdfFileName(null);
    setIsPdfLoading(false);
    
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/v1/private/provider/${kit.serial_number}/view-results`,
        {
          headers: {
            Authorization: `Bearer ${await AuthProvider.getValidAuthorizationToken()}`
          }
        }
      );

      if (!response.ok) {
        throw new Error(`Failed to fetch results: ${response.status}`);
      }

      const data = await response.json();
      if (data?.success && data?.data?.fileUrl) {
        setPdfUrl(data.data.fileUrl);
        setPdfFileName(data.data.fileName || `results_${kit.serial_number}.pdf`);
        // Hide loading immediately after getting the URL
        setIsLoadingPdf(false);
        // Show PDF loading state
        setIsPdfLoading(true);
      } else {
        setError('No PDF URL found in response');
        setIsLoadingPdf(false);
      }
    } catch (error) {
      console.error('Error fetching PDF URL:', error);
      setError('Failed to load results PDF. Please try again.');
      setIsLoadingPdf(false);
    }
  };

  const handleDownloadPdf = async () => {
    if (!pdfUrl || !selectedKitForViewResults) return;

    try {
      // Create a temporary link element to trigger download
      const link = document.createElement('a');
      link.href = pdfUrl;
      link.download = pdfFileName || `results_${selectedKitForViewResults.serial_number}.pdf`;
      link.target = '_blank';
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      console.error('Error downloading PDF:', error);
      notifications.error({
        message: 'Failed to download PDF. Please try again.'
      });
    }
  };

  const handleMarkAsCompletedClick = (kit: Kit) => {
    setSelectedKitForCompletion(kit);
    setConfirmModalOpen(true);
  };

  const handleConfirmCompletion = async () => {
    if (!selectedKitForCompletion || isCompletingKit) return;

    try {
      setIsCompletingKit(true);
      
      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/v1/private/provider/${selectedKitForCompletion.serial_number}/status/completed`,
        {
          method: 'PATCH',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${await AuthProvider.getValidAuthorizationToken()}`
          }
        }
      );

      if (!response.ok) {
        throw new Error(`Failed to mark result as completed: ${response.status}`);
      }

      // Refresh the kits list to show updated status
      fetchPage(currentPage, searchQuery, selectedFilter);
      setError(null);
      notifications.success({
        message: `Kit ${selectedKitForCompletion.serial_number} has been marked as completed.`
      });
      
      // Close the modal
      setConfirmModalOpen(false);
      setSelectedKitForCompletion(null);
    } catch (error) {
      console.error('Error marking result as completed:', error);
      setError('Failed to mark result as completed. Please try again.');
      notifications.error({
        message: 'Failed to mark result as completed. Please try again.'
      });
    } finally {
      setIsCompletingKit(false);
    }
  };

  const handleEditCustomerClick = (kit: Kit) => {
    setSelectedKitForEdit(kit);
    setEditCustomerModalOpen(true);
  };

  const handleUpdateCustomer = async (customerData: {
    firstName: string;
    lastName: string;
    phoneNumber: string;
    dateOfBirth: string;
    ssnLast4: string;
    address: string;
    addressLine2: string;
    city: string;
    state: string;
    zip: string;
  }) => {
    try {
      if (!selectedKitForEdit) {
        throw new Error('No kit selected for editing');
      }

      await updateRsaKitCustomer(selectedKitForEdit.serial_number, customerData);
      
      // Refresh the kits list to show updated data
      fetchPage(currentPage, searchQuery, selectedFilter);
      setError(null);
      notifications.success({
        message: `Patient details for ${customerData.firstName} ${customerData.lastName} have been updated successfully.`
      });
      
      // Close the modal after successful update
      setEditCustomerModalOpen(false);
      setSelectedKitForEdit(null);
    } catch (error: any) {
      console.error('Error updating customer details:', error);
      
      // Handle specific error cases
      let errorMessage = 'Failed to update patient details. Please try again.';
      
      if (error?.status === 409) {
        errorMessage = `A customer with phone number ${customerData.phoneNumber} already exists. Please use a different phone number.`;
      } else if (error?.status === 422) {
        errorMessage = 'Please check your input data and try again.';
      } else if (error?.status === 400) {
        errorMessage = 'Invalid request. Please check your input data.';
      } else if (error?.message) {
        errorMessage = error.message;
      }
      
      setError(errorMessage);
      notifications.error({
        message: errorMessage
      });
      throw error; // Re-throw to let the modal handle it
    }
  };

  if (loading && kits.length === 0) {
    return <LoadingSpinner />;
  }

  const tableSection = (
    <div className="table-container">
      <TableSection
        searchValue={searchQuery}
        onSearchChange={setSearchQuery}
        onSearch={handleSearch}
        onKeyDown={handleKeyDown}
        searchPlaceholder="Search by Patient Name / Phone / Date of Birth / Tracking Number"
        resultsCount={totalResults}
      >
        <KitsTable
          data={kits}
          columns={tableColumns}
          loading={loading}
          rowKey={(kit) => kit.serial_number}
          emptyMessage={<EmptyTableMessage searchQuery={searchQuery} selectedFilter={selectedFilter} />}
          loadingMessage="Loading results..."
          sortBy={sortBy}
          sortOrder={sortOrder}
          onSort={handleSort}
        />
      </TableSection>
    </div>
  );

  const headingActions = (
    <div className="rsa-order-section">
      <button
        className="create-rsa-order-btn"
        onClick={() => setRSAOrderModalOpen(true)}
        disabled={!provider?.sub || !clinic?.clinic_id}
        title={!provider?.sub || !clinic?.clinic_id ? "Provider and clinic info must be loaded" : ""}
      >
        <span className="btn-icon">+</span>
        <span className="btn-text">Order New RSA Kit</span>
      </button>
    </div>
  );

  let pdfViewerContent: React.ReactNode;

  if (isLoadingPdf) {
    pdfViewerContent = (
      <div className="pdf-loading">
        <div className="loading-spinner"></div>
        <p>Preparing PDF viewer...</p>
        <p className="loading-subtext">This will only take a moment</p>
      </div>
    );
  } else if (pdfUrl) {
    pdfViewerContent = (
      <div className="pdf-container">
        <div className="pdf-actions">
          <button 
            className="download-button"
            onClick={handleDownloadPdf}
          >
            View PDF File on New Tab
          </button>
        </div>
        <div className="pdf-viewer">
          {isPdfLoading && (
            <div className="pdf-iframe-loading">
              <div className="loading-spinner"></div>
              <p>Loading PDF content...</p>
            </div>
          )}
          <iframe
            src={pdfUrl}
            title="Results PDF"
            className="pdf-iframe"
            width="100%"
            height="600px"
            onLoad={() => setIsPdfLoading(false)}
            style={{ display: isPdfLoading ? 'none' : 'block' }}
          />
        </div>
      </div>
    );
  } else {
    pdfViewerContent = (
      <div className="pdf-error">
        <span className="error-icon">&#9888;</span>
        <p>No PDF results available for this patient.</p>
      </div>
    );
  }

  return (
    <DashboardLayout
      title="Patient Test Results"
      error={error}
      onDismissError={() => setError(null)}
      tableSection={tableSection}
      pagination={<Pagination totalPages={totalPages} currentPage={currentPage} onChange={handlePageChange} />}
      filterSidebar={
        <FilterSidebar
          options={FILTER_OPTIONS}
          selectedFilter={selectedFilter}
          onChange={handleFilterChange}
          clinic={clinic}
        />
      }
      headingActions={headingActions}
    >
      {/* Customer List Modal for Claiming */}
      <CustomerListModal
        isOpen={rsaOrderModalOpen}
        onClose={() => {
          setRSAOrderModalOpen(false);
        }}
        onClaim={handleRsaorderKit}
      />

      {/* Edit Customer Modal */}
      {selectedKitForEdit && (
        <EditCustomerModal
          isOpen={editCustomerModalOpen}
          onClose={() => {
            setEditCustomerModalOpen(false);
            setSelectedKitForEdit(null);
          }}
          onUpdate={handleUpdateCustomer}
          initialData={{
            firstName: selectedKitForEdit.customer_first_name,
            lastName: selectedKitForEdit.customer_last_name,
            phoneNumber: selectedKitForEdit.customer_phone?.replaceAll('+1', '') || '',
            dateOfBirth: selectedKitForEdit.customer_birthdate || null,
            ssnLast4: selectedKitForEdit.customer_ssn_last4 || null,
            address: selectedKitForEdit.customer_address || null,
            addressLine2: selectedKitForEdit.customer_address_line2 || null,
            city: selectedKitForEdit.customer_city || null,
            state: selectedKitForEdit.customer_state || null,
            zip: selectedKitForEdit.customer_zip || null,
          }}
        />
      )}

      {/* View Results Modal */}
      {viewResultsModalOpen && selectedKitForViewResults && (() => {
        const formattedAddress = selectedKitForViewResults.customer_address
          ? (() => {
              const addressLine1 = selectedKitForViewResults.customer_address;
              const addressLine2 = selectedKitForViewResults.customer_address_line2;
              return addressLine2 ? `${addressLine1}, ${addressLine2}` : addressLine1;
            })()
          : 'N/A';

        return (
          <div className="modal-overlay">
            <div className="view-results-modal">
              <div className="modal-header">
                <h3>View Results</h3>
                <button 
                  className="close-button"
                  onClick={() => {
                    setViewResultsModalOpen(false);
                    setSelectedKitForViewResults(null);
                    setPdfUrl(null);
                    setPdfFileName(null);
                    setIsPdfLoading(false);
                  }}
                >
                  &times;
                </button>
              </div>
              
              <div className="modal-content">
                <div className="kit-info-section">
                  <div className="info-section">
                    <h4>Patient Information</h4>
                    <div className="info-row">
                      <span className="label">Name:</span>
                      <span className="value">
                        {selectedKitForViewResults.customer_first_name && selectedKitForViewResults.customer_last_name 
                          ? `${capitalizeFirst(selectedKitForViewResults.customer_first_name)} ${capitalizeFirst(selectedKitForViewResults.customer_last_name)}`
                          : 'N/A'
                        }
                      </span>
                    </div>
                    <div className="info-row">
                      <span className="label">Phone:</span>
                      <span className="value">
                        {selectedKitForViewResults.customer_phone ? `${selectedKitForViewResults.customer_phone}` : 'N/A'}
                      </span>
                    </div>
                    <div className="info-row">
                      <span className="label">Date of Birth:</span>
                      <span className="value">
                        {selectedKitForViewResults.customer_birthdate
                          ? formatBirthdate(selectedKitForViewResults.customer_birthdate)
                          : 'N/A'}
                      </span>
                    </div>
                    <div className="info-row">
                      <span className="label">Last 4 SSN:</span>
                      <span className="value">
                        {selectedKitForViewResults.customer_ssn_last4 || 'N/A'}
                      </span>
                    </div>
                    <div className="info-row">
                      <span className="label">Address:</span>
                      <span className="value">
                        {formattedAddress}
                      </span>
                    </div>
                  <div className="info-row">
                    <span className="label">City, State ZIP:</span>
                    <span className="value">
                      {selectedKitForViewResults.customer_city && selectedKitForViewResults.customer_state && selectedKitForViewResults.customer_zip
                        ? `${selectedKitForViewResults.customer_city}, ${selectedKitForViewResults.customer_state} ${selectedKitForViewResults.customer_zip}`
                        : 'N/A'}
                    </span>
                  </div>
                </div>
              </div>
              
              <div className="pdf-viewer-section">
                {pdfViewerContent}
              </div>
            </div>
            
            <div className="modal-actions">
              <button 
                className="close-modal-button"
                onClick={() => {
                  setViewResultsModalOpen(false);
                  setSelectedKitForViewResults(null);
                  setPdfUrl(null);
                  setPdfFileName(null);
                  setIsPdfLoading(false);
                }}
              >
                Close
              </button>
            </div>
          </div>
        </div>
        );
      })()}

      {/* Confirmation Modal for Mark as Completed */}
      {confirmModalOpen && selectedKitForCompletion && (() => {
        const formattedAddress = selectedKitForCompletion.customer_address
          ? (() => {
              const addressLine1 = selectedKitForCompletion.customer_address;
              const addressLine2 = selectedKitForCompletion.customer_address_line2;
              return addressLine2 ? `${addressLine1}, ${addressLine2}` : addressLine1;
            })()
          : 'N/A';

        return (
          <div className="modal-overlay">
            <div className="confirmation-modal">
              <div className="modal-header">
                <h3>Confirm Completion</h3>
                <button 
                  className="close-button"
                  onClick={() => {
                    setConfirmModalOpen(false);
                    setSelectedKitForCompletion(null);
                  }}
                >
                  &times;
                </button>
              </div>
              
              <div className="modal-content">
                <div className="confirmation-content">
                  <div className="confirmation-icon">&#9888;</div>
                  <h4>Are you sure you want to mark this result as completed?</h4>
                  <div className="kit-details">
                    <p><strong>Patient Name:</strong> {
                      selectedKitForCompletion.customer_first_name && selectedKitForCompletion.customer_last_name 
                        ? `${capitalizeFirst(selectedKitForCompletion.customer_first_name)} ${capitalizeFirst(selectedKitForCompletion.customer_last_name)}`
                        : 'N/A'
                    }</p>
                    <p><strong>Patient Phone:</strong> {
                      selectedKitForCompletion.customer_phone 
                        ? `${selectedKitForCompletion.customer_phone}`
                        : 'N/A'
                    }</p>
                    <p><strong>Patient DOB:</strong> {
                      selectedKitForCompletion.customer_birthdate
                        ? formatBirthdate(selectedKitForCompletion.customer_birthdate)
                        : 'N/A'
                    }</p>
                    <p><strong>Last 4 SSN:</strong> {selectedKitForCompletion.customer_ssn_last4 || 'N/A'}</p>
                    <p><strong>Patient Address:</strong> {formattedAddress}</p>
                  <p><strong>City, State ZIP:</strong> {
                    selectedKitForCompletion.customer_city && selectedKitForCompletion.customer_state && selectedKitForCompletion.customer_zip
                      ? `${selectedKitForCompletion.customer_city}, ${selectedKitForCompletion.customer_state} ${selectedKitForCompletion.customer_zip}`
                      : 'N/A'
                  }                  </p>
                </div>
                <p className="confirmation-warning">
                  This action will update the result status to "Completed" and cannot be undone.
                </p>
              </div>
            </div>
            
            <div className="modal-actions">
              <button 
                className="cancel-button"
                onClick={() => {
                  setConfirmModalOpen(false);
                  setSelectedKitForCompletion(null);
                }}
                disabled={isCompletingKit}
              >
                Cancel
              </button>
              <button 
                className={`confirm-button ${isCompletingKit ? 'loading' : ''}`}
                onClick={handleConfirmCompletion}
                disabled={isCompletingKit}
              >
                {isCompletingKit ? (
                  <>
                    <div className="button-spinner"></div>
                    Processing...
                  </>
                ) : (
                  'Mark as Completed'
                )}
              </button>
            </div>
          </div>
        </div>
        );
      })()}
    </DashboardLayout>
  );
};

export default ProviderDashboard;